import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, ChevronRight, Bell } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const EVENT_TYPE_COLORS = {
  meeting: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  project: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  service: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  reminder: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  other: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' }
};

const EVENT_TYPE_ICONS = {
  meeting: '🤝',
  project: '📋',
  service: '🔧',
  reminder: '⏰',
  other: '📌'
};

export const UpcomingEvents = ({ limit = 5, userRole = 'admin' }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Determine which page to navigate to based on user role
  // Only admin/supervisor see full calendar, everyone else sees My Schedule
  const calendarPath = (userRole === 'admin' || userRole === 'supervisor') ? '/calendar' : '/my-schedule';

  const fetchUpcomingEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/events/upcoming`, {
        params: { limit }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  const formatEventTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    if (isToday) {
      return `Today at ${date.toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit' })}`;
    } else if (isTomorrow) {
      return `Tomorrow at ${date.toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit' });
    }
  };

  const getTimeUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = date - now;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `in ${days} day${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `in ${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      const minutes = Math.floor(diff / (1000 * 60));
      return minutes > 0 ? `in ${minutes} min` : 'now';
    }
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return null; // Don't show anything if no upcoming events
  }

  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-red-600" />
            Upcoming Events
          </h2>
          <button
            onClick={() => navigate(calendarPath)}
            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            View Calendar
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className="divide-y divide-gray-100">
        {events.map((event) => {
          const typeStyle = EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other;
          const typeIcon = EVENT_TYPE_ICONS[event.event_type] || '📌';
          
          return (
            <div
              key={event.id}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => navigate(calendarPath)}
            >
              <div className="flex items-start gap-3">
                {/* Event Color Bar */}
                <div 
                  className="w-1 h-full min-h-[60px] rounded-full flex-shrink-0"
                  style={{ backgroundColor: event.color || '#dc2626' }}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <span>{typeIcon}</span>
                        {event.title}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {formatEventTime(event.start_datetime)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeStyle.bg} ${typeStyle.text} whitespace-nowrap`}>
                      {getTimeUntil(event.start_datetime)}
                    </span>
                  </div>
                  
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </span>
                    )}
                    {event.project_name && (
                      <span className="flex items-center gap-1">
                        📋 {event.project_name}
                      </span>
                    )}
                    {event.attendee_details?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {event.attendee_details.length} attendee{event.attendee_details.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {events.length >= limit && (
        <div className="p-3 bg-gray-50 border-t border-gray-200">
          <button
            onClick={() => navigate(calendarPath)}
            className="w-full text-center text-sm text-red-600 hover:text-red-700 font-medium"
          >
            View All Events →
          </button>
        </div>
      )}
    </div>
  );
};

export default UpcomingEvents;
